import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface CapturePreviewData {
  front: string;
  back: string;
  top: string;
  left: string;
}

@Component({
  selector: 'app-capture-preview-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="capture-preview-dialog">
      <mat-dialog-content class="capture-preview-content">
        <!-- 합쳐진 이미지 표시 -->
        <div class="combined-image-container">
          <canvas #combinedCanvas class="combined-canvas"></canvas>
        </div>

        <!-- 개별 이미지들 (숨김 처리) -->
        <div class="hidden-images" style="display: none;">
          <img #topImg [src]="data.top" alt="Top View" />
          <img #leftImg [src]="data.left" alt="Left View" />
          <img #backImg [src]="data.back" alt="Back View" />
          <img #frontImg [src]="data.front" alt="Front View" />
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end" class="dialog-actions">
        <button mat-button class="download-btn" (click)="downloadCombinedImage()">
          <mat-icon>download</mat-icon>
          합쳐진 이미지 다운로드
        </button>
        <button mat-button mat-dialog-close class="close-btn">닫기</button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .capture-preview-dialog {
        width: 100%;
        height: 100%;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      }

      .dialog-title {
        margin: 0;
        padding: 16px 20px 0 20px;
        font-size: 20px;
        font-weight: 600;
        color: #1a1a1a;
        border-bottom: 1px solid #e0e0e0;
        padding-bottom: 12px;
      }

      .capture-preview-content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px 20px;
        max-height: calc(95vh - 120px);
      }

      .combined-image-container {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
        padding: 16px;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
        flex: 1;
      }

      .combined-canvas {
        max-width: 100%;
        max-height: 100%;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border: 2px solid #ffffff;
      }

      .hidden-images img {
        display: none;
      }

      .dialog-actions {
        padding: 12px 20px;
      }

      .download-btn {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-radius: 6px;
        padding: 6px 16px;
        margin-right: 10px;
        transition: all 0.2s ease-in-out;
      }

      .download-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }

      .close-btn {
        background: linear-gradient(135deg, #661111 0%, #661111 100%);
        color: #666;
        border-radius: 6px;
        padding: 6px 16px;
        transition: all 0.2s ease-in-out;
      }

      .close-btn:hover {
        background: #e0e0e0;
        color: #333;
      }

      /* 반응형 디자인 */
      @media (max-width: 768px) {
        .capture-preview-dialog {
          max-width: 98vw;
          max-height: 98vh;
        }

        .capture-preview-content {
          padding: 12px 16px;
          gap: 12px;
        }

        .combined-canvas {
          max-height: 60vh;
        }
      }

      @media (max-width: 480px) {
        .dialog-title {
          font-size: 18px;
          padding: 12px 12px 0 12px;
        }

        .capture-preview-content {
          padding: 8px 12px;
          gap: 8px;
        }

        .combined-canvas {
          max-height: 50vh;
        }
      }
    `,
  ],
})
export class CapturePreviewDialogComponent implements OnInit {
  @ViewChild('combinedCanvas', { static: false }) combinedCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('topImg', { static: false }) topImg!: ElementRef<HTMLImageElement>;
  @ViewChild('leftImg', { static: false }) leftImg!: ElementRef<HTMLImageElement>;
  @ViewChild('backImg', { static: false }) backImg!: ElementRef<HTMLImageElement>;
  @ViewChild('frontImg', { static: false }) frontImg!: ElementRef<HTMLImageElement>;

  constructor(
    public dialogRef: MatDialogRef<CapturePreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CapturePreviewData,
  ) {}

  ngOnInit(): void {
    // 이미지들이 로드된 후 캔버스에 그리기
    setTimeout(() => {
      this.drawCombinedImage();
    }, 100);
  }

  private drawCombinedImage(): void {
    if (!this.combinedCanvas || !this.topImg || !this.leftImg || !this.backImg || !this.frontImg) {
      return;
    }

    const canvas = this.combinedCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 이미지들의 실제 크기 측정
    const topImg = this.topImg.nativeElement;
    const leftImg = this.leftImg.nativeElement;
    const backImg = this.backImg.nativeElement;
    const frontImg = this.frontImg.nativeElement;

    // 이미지가 로드될 때까지 대기
    if (!topImg.complete || !leftImg.complete || !backImg.complete || !frontImg.complete) {
      setTimeout(() => this.drawCombinedImage(), 100);
      return;
    }

    // 각 이미지의 비율 계산
    const topRatio = topImg.naturalWidth / topImg.naturalHeight;
    const leftRatio = leftImg.naturalWidth / leftImg.naturalHeight;
    const backRatio = backImg.naturalWidth / backImg.naturalHeight;
    const frontRatio = frontImg.naturalWidth / frontImg.naturalHeight;

    // 캔버스 크기 설정 (이미지 비율에 맞게 조정)
    const canvasWidth = 800;
    let canvasHeight = 0;

    // Top 이미지 (90도 회전) - 회전 후 크기 계산
    // 90도 회전 시: 원본 너비 → 높이, 원본 높이 → 너비
    const topHeight = canvasWidth; // 원본 비율 유지하면서 회전
    const topWidth = topHeight * topRatio;

    // Left 이미지 높이
    const leftHeight = canvasWidth / leftRatio;

    // Back과 Front 이미지 높이 (각각 절반 너비)
    const backHeight = canvasWidth / 2 / backRatio;
    const frontHeight = canvasWidth / 2 / frontRatio;

    // 전체 높이 계산
    canvasHeight = topWidth + leftHeight + Math.max(backHeight, frontHeight);

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // 배경 설정
    ctx.fillStyle = '#6e7177';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 이미지들을 빈공간 없이 붙여서 그리기
    let currentY = 0;

    // Top 이미지 (90도 회전)
    this.loadAndDrawImage(ctx, topImg, 0, currentY, topHeight, topWidth, 90, topHeight / topWidth);
    currentY += topWidth;

    // Left 이미지
    this.loadAndDrawImage(ctx, leftImg, 0, currentY, canvasWidth, leftHeight, 0);
    currentY += leftHeight;

    // Back과 Front 이미지 (나란히 배치)
    const backWidth = canvasWidth / 2;
    const frontWidth = canvasWidth / 2;

    this.loadAndDrawImage(ctx, backImg, 0, currentY, backWidth, backHeight, 0);
    this.loadAndDrawImage(ctx, frontImg, backWidth, currentY, frontWidth, frontHeight, 0);
  }

  private loadAndDrawImage(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number = 0,
    scale: number = 1,
  ): void {
    if (img.complete) {
      this.drawImageOnCanvas(ctx, img, x, y, width, height, rotation, scale);
    } else {
      img.onload = () => {
        this.drawImageOnCanvas(ctx, img, x, y, width, height, rotation, scale);
      };
    }
  }

  private drawImageOnCanvas(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number = 0,
    scale: number = 1,
  ): void {
    ctx.save();

    // Top 이미지 (90도 회전)의 경우 원본 비율 그대로 유지
    if (rotation === 90) {
      // 원본 이미지 비율 그대로 사용
      const imgAspectRatio = img.naturalWidth / img.naturalHeight;
      let drawWidth = width;
      let drawHeight = height;

      // 회전 후 너비가 height가 되므로, 원본 높이를 기준으로 계산
      if (imgAspectRatio > 1) {
        // 원본이 가로로 긴 경우
        drawHeight = width / imgAspectRatio;
      } else {
        // 원본이 세로로 긴 경우
        drawWidth = height * imgAspectRatio;
      }

      // 회전 중심점 설정
      const centerX = x + width / 2;
      const centerY = y + height / 2;

      ctx.translate(centerX, centerY);
      ctx.rotate((rotation * Math.PI) / 180);

      // 이미지 그리기 (원본 비율 유지)
      ctx.scale(scale, scale);
      ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    } else {
      // 다른 이미지들은 기존 방식대로 처리
      const imgAspectRatio = img.naturalWidth / img.naturalHeight;
      const targetAspectRatio = width / height;

      let drawWidth = width;
      let drawHeight = height;

      // 비율에 맞게 크기 조정
      if (imgAspectRatio > targetAspectRatio) {
        // 이미지가 더 넓은 경우
        drawHeight = width / imgAspectRatio;
      } else {
        // 이미지가 더 높은 경우
        drawWidth = height * imgAspectRatio;
      }

      // 회전 중심점 설정
      const centerX = x + width / 2;
      const centerY = y + height / 2;

      ctx.translate(centerX, centerY);
      ctx.rotate((rotation * Math.PI) / 180);

      // 이미지 그리기 (비율 유지)
      ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    }

    ctx.restore();
  }

  downloadCombinedImage(): void {
    if (!this.combinedCanvas || !this.topImg || !this.leftImg || !this.backImg || !this.frontImg) {
      return;
    }

    // 다운로드용 고해상도 캔버스 생성 (2000px)
    const downloadCanvas = document.createElement('canvas');
    const downloadCtx = downloadCanvas.getContext('2d');
    if (!downloadCtx) return;

    const canvasWidth = 2000;
    let canvasHeight = 0;

    // 이미지들의 실제 크기 측정
    const topImg = this.topImg.nativeElement;
    const leftImg = this.leftImg.nativeElement;
    const backImg = this.backImg.nativeElement;
    const frontImg = this.frontImg.nativeElement;

    // 각 이미지의 비율 계산
    const topRatio = topImg.naturalWidth / topImg.naturalHeight;
    const leftRatio = leftImg.naturalWidth / leftImg.naturalHeight;
    const backRatio = backImg.naturalWidth / backImg.naturalHeight;
    const frontRatio = frontImg.naturalWidth / frontImg.naturalHeight;

    // Top 이미지 (90도 회전) - 회전 후 크기 계산
    const topHeight = canvasWidth;
    const topWidth = topHeight * topRatio;

    // Left 이미지 높이
    const leftHeight = canvasWidth / leftRatio;

    // Back과 Front 이미지 높이 (각각 절반 너비)
    const backHeight = canvasWidth / 2 / backRatio;
    const frontHeight = canvasWidth / 2 / frontRatio;

    // 전체 높이 계산
    canvasHeight = topWidth + leftHeight + Math.max(backHeight, frontHeight);

    downloadCanvas.width = canvasWidth;
    downloadCanvas.height = canvasHeight;

    // 배경 설정
    downloadCtx.fillStyle = '#6e7177';
    downloadCtx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 이미지들을 빈공간 없이 붙여서 그리기
    let currentY = 0;

    // Top 이미지 (90도 회전)
    this.loadAndDrawImage(
      downloadCtx,
      topImg,
      0,
      currentY,
      topHeight,
      topWidth,
      90,
      topHeight / topWidth,
    );
    currentY += topWidth;

    // Left 이미지
    this.loadAndDrawImage(downloadCtx, leftImg, 0, currentY, canvasWidth, leftHeight, 0);
    currentY += leftHeight;

    // Back과 Front 이미지 (나란히 배치)
    const backWidth = canvasWidth / 2;
    const frontWidth = canvasWidth / 2;

    this.loadAndDrawImage(downloadCtx, backImg, 0, currentY, backWidth, backHeight, 0);
    this.loadAndDrawImage(downloadCtx, frontImg, backWidth, currentY, frontWidth, frontHeight, 0);

    // 다운로드
    const link = document.createElement('a');
    link.download = 'combined_capture.png';
    link.href = downloadCanvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
